import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  OverlayView,
  DirectionsRenderer,
} from "@react-google-maps/api";
import {
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box
} from "@mui/material";
import DashboardHeader from "../../components/header/DashboardHeader";
import { loadOptions } from "../stops/mapConfig";
import config from "../../config.json";
// import BusIcon from "../../images/bus-svgrepo-com.svg";
import BusIcon from "../../images/vehicle/Map_Vehicle_active.svg";
import NavMenu from "../../components/navbar/NavMenu";
import TitleBar from "../../components/navbar/TitleBar";
import SubTitle from "../../components/common/SubTitle";

const colors = [
  "#20A8E0",
  "#8FBC8F",
  "#FFD700",
  "#6495ED", // Cornflower Blue
  "#FF69B4", // Hot Pink
  "#FFA07A", // Light Salmon
  "#ADFF2F", // Green Yellow
  "#FF4500", // Orange Red
  "#9370DB", // Medium Purple
  "#FFFFFF", // White (Test Color)
];

const mapStyles = [
  {
    featureType: "all",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ visibility: "on" }, { color: "black" }],
  },
];

const libraries = ["places", "directions"];

const normalizeDeviceData = (data) => {
  // If the data is already in the expected format, return as is
  if (data.deviceId && data.latitude && data.longitude) {
    return data;
  }

  // Handle the raw GNSS format
  return {
    deviceId: data.device_id,
    latitude: data.Latitude,
    longitude: data.Longitude,
    timestamp: new Date().toISOString(),
    rawData: data.RawData || "",
  };
};

// Add direction calculation helper functions
const calculateDistance = (p1, p2) => {
  if (!p1?.lat || !p2?.lat) return 0;

  const R = 6371e3; // Earth's radius in meters
  const φ1 = (p1.lat * Math.PI) / 180;
  const φ2 = (p2.lat * Math.PI) / 180;
  const Δφ = ((p2.lat - p1.lat) * Math.PI) / 180;
  const Δλ = ((p2.lng - p1.lng) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const TrackAllVehiclesSSE = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const allVehicleData = location.state?.allVehicleData || [];
  const [mapCenter, setMapCenter] = useState({ lat: 13.1986, lng: 77.7099 });
  const [allVehicle, setAllVehicle] = useState([]);
  const [labelPositions, setLabelPositions] = useState({});
  const [stops, setStops] = useState([]);
  const routePathsRef = useRef({});
  const [routes, setRoutes] = useState([]);
  const [directions, setDirections] = useState([]);
  const [map, setMap] = useState(null);
  const [routesByOrganization, setRoutesByOrganization] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [vehicleDirections, setVehicleDirections] = useState({});
  const [previousPositions, setPreviousPositions] = useState({});
  const [vehicleData, setVehicleData] = useState({});
  const [stoppedVehicles, setStoppedVehicles] = useState({});
  const parsedData = JSON.parse(localStorage.getItem("userState"));
  const orgId = parsedData?.user?.organizationId;

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const [vehicleIds, setVehicleIds] = useState([]);
  const [vehicleLocations, setVehicleLocations] = useState([]);
  const eventSourceRef = useRef(null);

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Add function to determine vehicle direction
  const determineVehicleDirection = (vehicle, routeStops) => {
    if (!vehicle || !routeStops || routeStops.length < 2) return false;

    const currentPosition = {
      lat: parseFloat(vehicle.latitude),
      lng: parseFloat(vehicle.longitude)
    };

    const source = {
      lat: parseFloat(routeStops[0].latitude),
      lng: parseFloat(routeStops[0].longitude)
    };

    const destination = {
      lat: parseFloat(routeStops[routeStops.length - 1].latitude),
      lng: parseFloat(routeStops[routeStops.length - 1].longitude)
    };

    // Get previous positions for this vehicle
    const vehiclePrevPositions = previousPositions[vehicle.deviceId] || [];

    // Calculate distances
    const distanceToSource = calculateDistance(currentPosition, source);
    const distanceToDestination = calculateDistance(currentPosition, destination);

    // Update previous positions
    setPreviousPositions(prev => ({
      ...prev,
      [vehicle.deviceId]: [
        ...vehiclePrevPositions.slice(-4),
        currentPosition
      ]
    }));

    // Determine direction based on position relative to route endpoints
    return distanceToSource > distanceToDestination;
  };

  // Function to fetch stops for a specific route coordinate
  const fetchStopsForRoute = async (route) => {
    const stops = [];
    for (const coordinate of route.routeCoordinates) {
      try {
        const stopsResponse = await axios.get(
          `${config.baseURL}/stop/organization/location`,
          {
            params: {
              organizationId: orgId,
              longitude: coordinate.longitude,
              latitude: coordinate.latitude,
            },
            withCredentials: true,
          }
        );
        stops.push(stopsResponse.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          console.log("Unauthorized request");
          navigate("/");
          return;
        }
        console.log("Error in fetching stops by coordinates", error);
      }
    }
    return stops;
  };

  // Fetch routes and their associated stops
  useEffect(() => {
    async function fetchRoutes() {
      setIsLoading(true);
      try {
        const routesResponse = await axios.get(
          `${config.baseURL}/route/organization`,
          {
            params: {
              organizationId: orgId || 1,
            },
            withCredentials: true,
          }
        );

        if (routesResponse.status === 401) {
          navigate("/");
          return;
        }

        const routesWithStops = await Promise.all(
          routesResponse.data.map(async (route) => {
            const stops = await fetchStopsForRoute(route);
            return { ...route, stops };
          })
        );

        setRoutesByOrganization(routesWithStops);
        setRoutes(routesWithStops);

        // Extract all unique stops from routes
        const allStops = routesWithStops.flatMap((route) =>
          route.stops
            .flat()
            .filter(
              (stop, index, self) =>
                index === self.findIndex((s) => s.id === stop.id)
            )
        );
        setStops(allStops);
      } catch (error) {
        if (error.response?.status === 401) {
          navigate("/");
          return;
        }
        console.error("Error fetching routes:", error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchRoutes();
  }, [navigate, orgId]);

  // Add this function to determine direction arrow
  const getDirectionArrow = (deviceId) => {
    const isInbound = vehicleDirections[deviceId];
    const isStopped = stoppedVehicles[deviceId];

    if (isStopped) {
      return "●"; // dot for stopped vehicles
    }

    // Return arrow based on direction
    return isInbound ? "→" : "←";
  };

  // Modify the fetchVehicleLocation function to include direction calculation
  const fetchVehicleLocation = async (deviceId) => {
    try {
      const response = await fetch(`${config.baseURL}/location/latest/${deviceId}`);
      const data = await response.json();

      if (!data || typeof data.latitude === 'undefined' || typeof data.longitude === 'undefined') {
        return null;
      }

      // Store the current position
      const currentPosition = {
        lat: parseFloat(data.latitude),
        lng: parseFloat(data.longitude)
      };

      // Get previous position
      const prevPositions = previousPositions[deviceId] || [];

      // Check if vehicle has moved
      if (prevPositions.length > 0) {
        const lastPosition = prevPositions[prevPositions.length - 1];
        const distance = calculateDistance(currentPosition, lastPosition);

        // Update stopped status (if movement is less than 10 meters)
        setStoppedVehicles(prev => ({
          ...prev,
          [deviceId]: distance < 10
        }));
      }

      // Update vehicle data
      setVehicleData(prev => ({
        ...prev,
        [deviceId]: {
          position: currentPosition,
          timestamp: new Date().getTime()
        }
      }));

      // Calculate direction if we have stops
      const isInbound = stops.length > 0 ? determineVehicleDirection(
        { deviceId, latitude: data.latitude, longitude: data.longitude },
        stops
      ) : false;

      setVehicleDirections(prev => ({
        ...prev,
        [deviceId]: isInbound
      }));

      return {
        deviceId,
        latitude: data.latitude,
        longitude: data.longitude,
      };
    } catch (error) {
      console.error(`Error fetching location for device ${deviceId}:`, error);
      return null;
    }
  };

  // Update directions calculation to use routesByOrganization
  useEffect(() => {
    if (isLoaded && routesByOrganization.length > 0 && map) {
      const directionsService = new window.google.maps.DirectionsService();

      const fetchDirections = async () => {
        const directionsPromises = routesByOrganization.map((route) => {
          return new Promise((resolve, reject) => {
            const waypoints = route.routeCoordinates
              .slice(1, -1)
              .map((coord) => ({
                location: new window.google.maps.LatLng(
                  parseFloat(coord.latitude),
                  parseFloat(coord.longitude)
                ),
                stopover: true,
              }));

            const origin = new window.google.maps.LatLng(
              parseFloat(route.routeCoordinates[0].latitude),
              parseFloat(route.routeCoordinates[0].longitude)
            );
            const destination = new window.google.maps.LatLng(
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1]
                  .latitude
              ),
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1]
                  .longitude
              )
            );

            directionsService.route(
              {
                origin: origin,
                destination: destination,
                waypoints: waypoints,
                travelMode: window.google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                  resolve({ ...result, routeColor: route.routeColor });
                } else {
                  reject(`error fetching directions ${result}`);
                }
              }
            );
          });
        });

        try {
          const results = await Promise.all(directionsPromises);
          setDirections(results);
        } catch (error) {
          console.error(error);
        }
      };

      fetchDirections();
    }
  }, [isLoaded, routesByOrganization, map]);

  const fetchAllVehicleLocations = useCallback(() => {
    if (vehicleIds.length === 0) return;
    // Build the query string with multiple vehicleId parameters
    const query = vehicleIds.map((id) => `vehicleId=${id}`).join("&");

    // Close any existing connection if necessary
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    const sseUrl = `${config.baseURL}/redis/stream?${query}`;
    const source = new EventSource(sseUrl);
    eventSourceRef.current = source;

    source.onopen = () => {
      console.log("Persistent SSE connection established");
    };

    source.onerror = (error) => {
      console.error("Persistent SSE error:", error);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };

    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type && data.type === "connected") {
          console.log("Received connection confirmation:", data);
          return;
        }
        if (data.device_id && data.Latitude && data.Longitude) {
          const normalizedData = normalizeDeviceData(data);

          setVehicleLocations((prev) => {
            // Check if this device already exists in our state
            const existingIndex = prev.findIndex(
              (item) => item.deviceId === normalizedData.deviceId
            );

            if (existingIndex >= 0) {
              // Update existing device
              const newLocations = [...prev];
              newLocations[existingIndex] = normalizedData;
              return newLocations;
            } else {
              // Add new device
              return [...prev, normalizedData];
            }
          });
        } else if (!Array.isArray(data)) {
          setVehicleLocations((prev) => {
            const existingIndex = prev.findIndex(
              (item) => item.deviceId === data.deviceId
            );

            if (existingIndex >= 0) {
              const newLocations = [...prev];
              newLocations[existingIndex] = data;
              return newLocations;
            } else {
              return [...prev, data];
            }
          });
        } else if (data.locations && Array.isArray(data.locations)) {
          setVehicleLocations((prev) => {
            const newLocations = [...prev];

            data.locations.forEach((location) => {
              const existingIndex = newLocations.findIndex(
                (item) => item.deviceId === location.deviceId
              );

              if (existingIndex >= 0) {
                newLocations[existingIndex] = location;
              } else {
                newLocations.push(location);
              }
            });

            return newLocations;
          });
        }
        // Handle simple array format
        else if (Array.isArray(data)) {
          const newLocations = [];

          data.forEach((location) => {
            if (location.device_id && location.Latitude && location.Longitude) {
              newLocations.push(normalizeDeviceData(location));
            } else if (
              location.deviceId &&
              location.latitude &&
              location.longitude
            ) {
              newLocations.push(location);
            }
          });

          if (newLocations.length > 0) {
            setVehicleLocations(newLocations);
          }
        } else {
          console.warn("Received SSE data in unexpected format:", data);
        }
      } catch (e) {
        console.error("Error parsing SSE data:", e);
      }
    };
  }, [vehicleIds]);

  useEffect(() => {
    if (vehicleIds.length > 0) {
      fetchAllVehicleLocations();
      return () => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }
      };
    }

  }, [vehicleIds, fetchAllVehicleLocations]);

  // Calculate direction for real-time vehicle data
  useEffect(() => {
    if (vehicleLocations.length > 0 && stops.length > 0) {
      vehicleLocations.forEach(vehicle => {
        if (!vehicle) return;

        const deviceId = vehicle.deviceId || vehicle.device_id;
        if (!deviceId) return;

        const currentPosition = {
          lat: parseFloat(vehicle.latitude || vehicle.Latitude),
          lng: parseFloat(vehicle.longitude || vehicle.Longitude)
        };

        // Get previous position
        const prevPositions = previousPositions[deviceId] || [];

        // Check if vehicle has moved
        if (prevPositions.length > 0) {
          const lastPosition = prevPositions[prevPositions.length - 1];
          const distance = calculateDistance(currentPosition, lastPosition);

          // Update stopped status (if movement is less than 10 meters)
          setStoppedVehicles(prev => ({
            ...prev,
            [deviceId]: distance < 10
          }));
        }

        // Update previous positions
        setPreviousPositions(prev => ({
          ...prev,
          [deviceId]: [
            ...(prev[deviceId] || []).slice(-4),
            currentPosition
          ]
        }));

        // Calculate direction
        const isInbound = determineVehicleDirection(
          {
            deviceId,
            latitude: vehicle.latitude || vehicle.Latitude,
            longitude: vehicle.longitude || vehicle.Longitude
          },
          stops
        );

        setVehicleDirections(prev => ({
          ...prev,
          [deviceId]: isInbound
        }));
      });
    }
  }, [vehicleLocations, stops]);

  useEffect(() => {
    if (allVehicleData.length === 0) return;

    // Extract vehicle IDs from allVehicleData
    const ids = allVehicleData.map(vehicle => vehicle.device_id);
    setVehicleIds(ids);

    const updateLocations = async () => {
      setIsLoadingVehicles(true);
      try {
        // Instead of fetching, just set the IDs for EventSource to use
        setVehicleIds(allVehicleData.map(vehicle => vehicle.device_id));
        setIsLoadingVehicles(false);
      } catch (error) {
        console.error("Error setting vehicle IDs:", error);
        setIsLoadingVehicles(false);
      }
    };

    // Initial update
    updateLocations();

    // No need for interval as EventSource will handle updates
  }, [allVehicleData]);

  useEffect(() => {
    if (selectedVehicles.length === 0) {
      setFilteredVehicles(allVehicle); // Show all vehicles if nothing is selected
    } else {
      const filtered = allVehicle.filter((vehicle) =>
        selectedVehicles.includes(vehicle.deviceId)
      );
      setFilteredVehicles(filtered);
    }

    // Make sure we have vehicle IDs for SSE
    if (allVehicle.length > 0) {
      const extractVehicleIds = allVehicle.map((item) => item.deviceId);
      setVehicleIds(prevIds => {
        // Only update if different to avoid unnecessary re-renders
        if (JSON.stringify(prevIds) !== JSON.stringify(extractVehicleIds)) {
          return extractVehicleIds;
        }
        return prevIds;
      });
    }
  }, [allVehicle, selectedVehicles]);

  const handleVehicleChange = (event) => {
    const {
      target: { value },
    } = event;

    const selectedValues = typeof value === "string" ? value.split(",") : value;
    setSelectedVehicles(selectedValues);
  };

  const calculateLabelPosition = (vehicles) => {
    const positions = {};
    const occupiedSpaces = new Set();
    const LABEL_HEIGHT = 30;

    vehicles.forEach((vehicle) => {
      if (!vehicle || !vehicle.latitude || !vehicle.longitude) return;

      const basePosition = 14;
      let currentPosition = basePosition;

      while (
        occupiedSpaces.has(
          `${vehicle.latitude}-${vehicle.longitude}-${currentPosition}`
        )
      ) {
        currentPosition += LABEL_HEIGHT;
      }

      positions[vehicle.deviceId] = currentPosition;
      occupiedSpaces.add(
        `${vehicle.latitude}-${vehicle.longitude}-${currentPosition}`
      );
    });

    return positions;
  };

  const renderVehicleMarker = (vehicle) => {
    if (!vehicle || !vehicle.latitude || !vehicle.longitude) return null;
    const directionArrow = getDirectionArrow(vehicle.deviceId);

    return (
      <div key={vehicle.deviceId}>
        <OverlayView
          position={{
            lat: parseFloat(vehicle.latitude),
            lng: parseFloat(vehicle.longitude),
          }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div
            style={{
              position: "relative",
              top: "-90px",
              left: "0px",
              width: "2px",
              height: "2px",
            }}
          >
            <div
              style={{
                position: "absolute",
                width: "2px",
                height: "72px",
                top: "10px",
                backgroundColor: getBackgroundColor(vehicle.deviceId),
                bottom: "9px",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: "-10px",
                left: "50%",
                transform: "translateX(-50%)",
                color: "rgb(0, 0, 0)",
                fontSize: "10px",
                fontWeight: 600,
                fontFamily: "Arial, sans-serif",
                lineHeight: "14.06px",
                padding: "8px",
                textAlign: "left",
                whiteSpace: "nowrap",
                backgroundColor: getBackgroundColor(vehicle.deviceId),
                borderRadius: "12px",
                zIndex: 1000,
                border: "1px solid black",
              }}
            >
              {vehicle.deviceId} {directionArrow}
            </div>
          </div>
        </OverlayView>
        <Marker
          position={{
            lat: parseFloat(vehicle.latitude),
            lng: parseFloat(vehicle.longitude),
          }}
          icon={{
            url: BusIcon,
            scaledSize: new window.google.maps.Size(30, 30),
            anchor: new window.google.maps.Point(15, 15),
          }}
        />
      </div>
    );
  };

  const renderStop = (stop) => {
    if (!stop.latitude || !stop.longitude) return null;

    const position = {
      lat: parseFloat(stop.latitude),
      lng: parseFloat(stop.longitude),
    };

    return (
      <div>
        <OverlayView
          position={position}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div
            style={{
              position: "relative",
              top: "-62px",
              left: "0px",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "white",
                color: "black",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: "500",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                whiteSpace: "nowrap",
                zIndex: 1000,
              }}
            >
              {stop.name}
            </div>
          </div>
        </OverlayView>
        <Marker position={position} title={stop.name} />
      </div>
    );
  };

  const renderVehicleSelect = () => (
    <FormControl
      size="small"
      sx={{
        position: "absolute",
        top: "40px",
        right: "40px",
        width: 200,
        zIndex: 104,
        backgroundColor: "white",
        borderRadius: "8px",
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: "#000",
            borderRadius: "8px",
          },
          "&:hover fieldset": {
            borderColor: "#000",
          },
        },
        scrollbarWidth: "thin",
        scrollbarColor: "#888 transparent",
        "& .MuiSelect-select": {
          "&::-webkit-scrollbar": {
            width: "4px",
            height: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#666",
          },
        },
      }}
    >
      <Select
        multiple
        displayEmpty
        value={selectedVehicles}
        onChange={handleVehicleChange}
        input={<OutlinedInput />}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return "Select Vehicles";
          }
          return `${selected.length} selected`;
        }}
      >
        {allVehicleData.map((vehicle) => (
          <MenuItem
            key={vehicle.device_id}
            value={vehicle.device_id}
            sx={{
              borderRadius: "4px",
              margin: "2px 4px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <Checkbox
              checked={selectedVehicles.includes(vehicle.device_id)}
              sx={{ padding: "0", marginBottom: "5px" }}
            />
            <ListItemText
              primary={vehicle.device_id}
              sx={{
                "& .MuiTypography-root": {
                  color: "#00000",
                  fontSize: "14px",
                },
              }}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // If maps API isn't loaded yet, show loading state
  if (!isLoaded) {
    return (
      <div>
        <TitleBar />
        <NavMenu />

        <SubTitle
          title={"Monitor Vehicles"}
          path={"Vehicles > Monitor Vehicles"} />
        Loading Maps...
      </div>
    )
  }

  if (loadError) {
    return (<div>
      <TitleBar />
      <NavMenu />

      <SubTitle
        title={"Monitor Vehicles"}
        path={"Vehicles > Monitor Vehicles"} />
      Error loading maps: {loadError.message}</div>
    )
  }

  return (
    <>
      {/* <DashboardHeader title="Vehicle > TrackAllVehicle" /> */}
      <TitleBar />
      <NavMenu />

      <SubTitle
        title={"Monitor Vehicles"}
        path={"Vehicles > Monitor Vehicles"} />
      <Box
        // className="page_container"

        sx={{
          border: "none", position: "relative"
          ,
          margin: { xs: "0px 20px", md: "0px 40px" },
          boxShadow: "-2px 1px 5px 1px #00000040",
          borderRadius: "10px",
          padding: "20px",
          border: "1px solid #A5DCF3"
        }}
      >
        {renderVehicleSelect()}

        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            height: "90vh",
            borderRadius: "20px"
          }}
          center={mapCenter}
          zoom={14}
          zIndex={100}
          options={{
            styles: mapStyles,
          }}
          onLoad={onLoad}
          onUnmount={onUnmount}
        >
          {/* Render Stops */}
          {stops.map(renderStop)}

          {/* First render vehicles from filteredVehicles (initial fetch) */}
          {filteredVehicles.map(renderVehicleMarker)}

          {/* Then render real-time SSE vehicle data */}
          {vehicleLocations
            .filter(vehicle =>
              vehicle &&
              (vehicle.latitude || vehicle.Latitude) &&
              (vehicle.longitude || vehicle.Longitude) &&
              (selectedVehicles.length === 0 || selectedVehicles.includes(vehicle.deviceId || vehicle.device_id))
            )
            .map(vehicle => {
              const deviceId = vehicle.deviceId || vehicle.device_id;
              const directionArrow = getDirectionArrow(deviceId);

              return (
                <React.Fragment key={deviceId}>
                  <OverlayView
                    position={{
                      lat: parseFloat(vehicle.latitude || vehicle.Latitude),
                      lng: parseFloat(vehicle.longitude || vehicle.Longitude),
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div
                      style={{
                        position: "relative",
                        top: "-90px",
                        left: "0px",
                        width: "2px",
                        height: "2px",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          width: "2px",
                          height: "72px",
                          top: "10px",
                          backgroundColor: getBackgroundColor(deviceId),

                          bottom: "9px",
                          left: "50%",
                          transform: "translateX(-50%)",
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: "-10px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          color: "rgb(0, 0, 0)",
                          fontSize: "10px",
                          fontWeight: 600,
                          fontFamily: "Arial, sans-serif",
                          lineHeight: "14.06px",
                          padding: "8px",
                          textAlign: "left",
                          whiteSpace: "nowrap",
                          backgroundColor: getBackgroundColor(deviceId),
                          borderRadius: "12px",
                          zIndex: 1000,
                          border: "1px solid black",
                        }}
                      >
                        {deviceId} {directionArrow}
                      </div>
                    </div>
                  </OverlayView>
                  <Marker
                    position={{
                      lat: parseFloat(vehicle.latitude || vehicle.Latitude),
                      lng: parseFloat(vehicle.longitude || vehicle.Longitude),
                    }}
                    icon={{
                      url: BusIcon,
                      scaledSize: new window.google.maps.Size(30, 30),
                      anchor: new window.google.maps.Point(15, 15),
                    }}
                  />
                </React.Fragment>
              );
            })}

          {directions.map((direction, i) => (
            <DirectionsRenderer
              key={i}
              directions={direction}
              options={{
                polylineOptions: {
                  strokeColor: direction.routeColor || colors[i % colors.length],
                  strokeOpacity: 0.7,
                  strokeWeight: 5,
                },
                suppressMarkers: true,
              }}
            />
          ))}
        </GoogleMap>
      </Box>
    </>
  );
};

// Function to get background color for vehicle marker
const getBackgroundColor = (deviceId) => {
  // If no deviceId, return default color
  if (!deviceId) return colors[0];

  // Use hash function to determine color index based on deviceId
  const hashCode = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  };

  const colorIndex = hashCode(deviceId) % colors.length;
  return colors[colorIndex];
};

export default TrackAllVehiclesSSE;

